import { FC } from 'react';
import styled from '@emotion/styled';
import { Button } from 'components/core/button';
import { Text } from 'components/core/text';
import { borderRadius, spacing } from 'components/core/theme';
import { mediaDesktop } from 'components/core/theme/breakpoints';

import { LeadProgram } from '../constants';

export const Modal = styled.div`
  position: fixed;
  z-index: 1;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background-color: rgb(0, 0, 0);
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 0;
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: ${borderRadius.m};
  margin: 0 auto;
  padding: 40px;
  border: 1px solid #888;
  width: 100%;

  ${mediaDesktop} {
    width: 720px;
  }
`;

export const ModalText = styled.div`
  padding: 2em 0;
  color: #6e6e6e;
`;

export const DialogActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: ${spacing.xs};
`;

interface ReferralExclusionModalProps {
  setShowModal: (show: boolean) => void;
  program: LeadProgram;
}

const commonExclusions = [
  'Detox for alcohol or illicit drug use',
  'Active suicidal ideation with a plan within the last 2 weeks',
  'Active self-injury within the last 2 weeks',
  'Active command hallucinations, severe thought disorganization',
  'Neurocognitive disorder who have severe memory or functioning difficulties',
  'Severe eating disorders or a BMI less than 18 due to an eating disorder',
];

const npExclusions = [
  'Active command hallucinations, severe thought disorganization',
  'Neurocognitive disorder who have severe memory or functioning difficulties',
  'BMI of less than 18 due to an eating disorder',
  'Custody evaluations or court ordered evaluations',
  'Tuberous Sclerosis Complex (TSC)',
  'Severe Intellectual Disabilities',
  'Non-verbal Autism Spectrum Disorder (ASD)',
  'Severe Epilepsy Syndromes (e.g., Dravet Syndrome)',
  'Progressive Neurological Conditions (e.g., Batten Disease)',
  'Severe Psychogenic Non-Epileptic Seizures (PNES)',
  'Photosensitive Epilepsy and Screen Risks i.e. sensitivity to Flashing/Flickering Lights, Bright/High-Contrast Screens, Rapid Visual Changes, etc.',
  'Extremely limited access to technology',
  'Severe impulsivity or aggression impairing ability to engage in virtual testing',
  'Testing for fine motor coordination',
];

const PROGRAM_EXCLUSIONS = {
  asp: [
    ...commonExclusions,
    'Severe ASD (inability/limited ability to vocalize)',
  ],
  np: [...npExclusions],
  cc: [
    ...commonExclusions,
    'Active child protection cases or concerns',
    'Complex custodial situations, including ongoing disputes or custody conflict',
  ],
} as const;

export const ReferralExclusionModal: FC<ReferralExclusionModalProps> = ({
  setShowModal,
  program,
}) => {
  return (
    <Modal onClick={() => setShowModal(false)}>
      <ModalContent>
        <Text variant="heading4">List of exclusions</Text>
        <ModalText>
          <ul className="list-disc px-8">
            {PROGRAM_EXCLUSIONS[program].map(exclusion => (
              <li key={exclusion}>{exclusion}</li>
            ))}
          </ul>
        </ModalText>

        <DialogActions>
          <Button onClick={() => setShowModal(false)}>Close</Button>
        </DialogActions>
      </ModalContent>
    </Modal>
  );
};
